import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import CloseIcon from "@mui/icons-material/Close";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

const PIE_COLORS = [
  "#002169", "#059669", "#9333ea", "#f59e0b",
  "#ef4444", "#3b82f6", "#ec4899", "#64748b",
  "#14b8a6", "#f97316", "#a855f7", "#0ea5e9",
];

function ChartCard({ title, children }) {
  return (
    <Box sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", display: "flex", flexDirection: "column", gap: 1 }}>
      {title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BLUE }}>
          {title}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function fmtDateAU(iso) {
  if (!iso) return "—";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

function fmtDollars(v) {
  return "$" + Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const TAB_LABELS = ["SMS Billing", "Terminal Analytics", "Dispense Systems", "Installations", "Engagement"];

/* ── SMS Billing tab ─────────────────────────────────────────────────── */
function SmsBillingTab({ data }) {
  const summary = data?.SmsBillingSummary ?? {};
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {[
          { label: "Current value (pending charge)", value: fmtDollars((summary.TotalChargesDollars ?? 0) / 100) },
          { label: "Pharmacies with pending SMS", value: Number(summary.ChemistsCharged ?? 0).toLocaleString() },
          { label: "Pending SMS count", value: Number(summary.TotalSMS ?? 0).toLocaleString() },
        ].map(({ label, value }) => (
          <Box key={label} sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", minWidth: 180 }}>
            <Typography variant="caption" color="textSecondary">{label}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: BLUE }}>{value}</Typography>
          </Box>
        ))}
      </Box>
      <ChartCard title="Monthly charge history (last 24 months)">
        {data?.SmsMonthlyCharges?.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.SmsMonthlyCharges} margin={{ top: 4, right: 8, left: 8, bottom: 44 }}>
              <XAxis
                dataKey="month"
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
                interval={Math.ceil(data.SmsMonthlyCharges.length / 8) - 1}
                angle={-45}
                textAnchor="end"
              />
              <YAxis
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
              />
              <ReTooltip
                contentStyle={{ fontSize: 11 }}
                formatter={(v) => [`$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, "Value"]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="totalDollars"
                name="SMS billing ($)"
                stroke={BLUE}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="textSecondary">No SMS billing data</Typography>
        )}
      </ChartCard>
    </Box>
  );
}

/* ── Terminal Analytics tab ──────────────────────────────────────────── */
function TerminalAnalyticsTab({ termData, termLoading, termError, onLoad }) {
  const [lowResOpen, setLowResOpen] = useState(null);
  const [oldVerOpen, setOldVerOpen] = useState(null);

  if (termLoading && !termData) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
        <Box sx={{ textAlign: "center" }}>
          <Box className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3" sx={{ borderColor: BLUE }} />
          <Typography color="textSecondary">Loading terminal analytics…</Typography>
        </Box>
      </Box>
    );
  }

  if (termError) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Alert severity="warning">{termError}</Alert>
        <Button variant="outlined" onClick={onLoad} sx={{ alignSelf: "flex-start", textTransform: "none", borderColor: BLUE, color: BLUE }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!termData) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Stat pill */}
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          p: 2,
          borderRadius: 2,
          border: `2px solid ${BLUE}`,
          backgroundColor: "#eef1f9",
          gap: 0.25,
          alignSelf: "flex-start",
          minWidth: 160,
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Active Terminals
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, color: BLUE, lineHeight: 1.2 }}>
          {Number(termData.TotalTerminals).toLocaleString()}
        </Typography>
        {termData.MaxVersion && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Max version: {termData.MaxVersion}
          </Typography>
        )}
      </Box>

      {/* Row 1: Version pie (1/3) + pharmacies not on latest version (2/3) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" }, gap: 2, alignItems: "stretch" }}>
        <ChartCard title="Version distribution">
          {termData.VersionPie?.length > 0 ? (
            <ResponsiveContainer width="100%" height={380}>
              <PieChart>
                <Pie
                  data={termData.VersionPie}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {termData.VersionPie.map((entry, i) => (
                    <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip formatter={(v, name) => [v, name]} contentStyle={{ fontSize: 11 }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="textSecondary">No data</Typography>
          )}
        </ChartCard>

        <ChartCard title={`Pharmacies not on latest version (${termData.MaxVersion ?? "?"}) — ${termData.OldVersionChemists?.length ?? 0} pharmacies`}>
          {termData.OldVersionChemists?.length > 0 ? (
            <Box sx={{ height: 380 }}>
              <DataGrid
                rows={termData.OldVersionChemists.map((c, i) => ({
                  id: i,
                  chemistName: c.chemistName || "—",
                  terminalCount: c.terminals?.length ?? 0,
                }))}
                columns={[
                  { field: "chemistName", headerName: "Pharmacy", flex: 1 },
                  { field: "terminalCount", headerName: "Old-version PCs", width: 140, type: "number", align: "center", headerAlign: "center" },
                  {
                    field: "actions", headerName: "Details", width: 100, sortable: false, align: "center", headerAlign: "center",
                    renderCell: (p) => (
                      <Button size="small" variant="outlined" onClick={() => setOldVerOpen(termData.OldVersionChemists[p.row.id])}
                        sx={{ textTransform: "none", borderColor: BLUE, color: BLUE, fontSize: "0.75rem" }}>
                        View
                      </Button>
                    ),
                  },
                ]}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={pharmacyHaloDataGridSx}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">All active terminals are on the latest version.</Typography>
          )}
        </ChartCard>
      </Box>

      {/* Row 2: Resolution pie (1/3) + low-res pharmacies (2/3) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" }, gap: 2, alignItems: "stretch" }}>
        <ChartCard title="Screen resolution distribution">
          {termData.ResolutionPie?.length > 0 ? (
            <ResponsiveContainer width="100%" height={380}>
              <PieChart>
                <Pie
                  data={termData.ResolutionPie}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {termData.ResolutionPie.map((entry, i) => (
                    <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip contentStyle={{ fontSize: 11 }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="textSecondary">No data</Typography>
          )}
        </ChartCard>

        <ChartCard title={`Pharmacies with low-res monitors (<1920×1080) — ${termData.LowResChemists?.length ?? 0} pharmacies`}>
          {termData.LowResChemists?.length > 0 ? (
            <Box sx={{ height: 380 }}>
              <DataGrid
                rows={termData.LowResChemists.map((c, i) => ({
                  id: i,
                  chemistName: c.chemistName || "—",
                  terminalCount: c.terminals?.length ?? 0,
                }))}
                columns={[
                  { field: "chemistName", headerName: "Pharmacy", flex: 1 },
                  { field: "terminalCount", headerName: "Low-res PCs", width: 120, type: "number", align: "center", headerAlign: "center" },
                  {
                    field: "actions", headerName: "Details", width: 100, sortable: false, align: "center", headerAlign: "center",
                    renderCell: (p) => (
                      <Button size="small" variant="outlined" onClick={() => setLowResOpen(termData.LowResChemists[p.row.id])}
                        sx={{ textTransform: "none", borderColor: BLUE, color: BLUE, fontSize: "0.75rem" }}>
                        View
                      </Button>
                    ),
                  },
                ]}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={pharmacyHaloDataGridSx}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No pharmacies with low-res monitors.</Typography>
          )}
        </ChartCard>
      </Box>

      {/* Low-res detail modal */}
      <Dialog open={!!lowResOpen} onClose={() => setLowResOpen(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700, color: BLUE }}>
          {lowResOpen?.chemistName} — Low-res monitors
          <IconButton size="small" onClick={() => setLowResOpen(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Computer</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Resolution</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(lowResOpen?.terminals ?? []).map((t, i) => (
                <TableRow key={i}>
                  <TableCell><Typography variant="body2">{t.compName || "—"}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip label={`${t.ScreenWidth}×${t.ScreenHeight}`} size="small" color="warning" variant="outlined" sx={{ fontSize: "0.72rem" }} />
                  </TableCell>
                  <TableCell><Typography variant="body2">{t.version || "—"}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{fmtDateAU(t.lastAccess)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLowResOpen(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Old-version detail modal */}
      <Dialog open={!!oldVerOpen} onClose={() => setOldVerOpen(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700, color: BLUE }}>
          {oldVerOpen?.chemistName} — Terminal versions
          <IconButton size="small" onClick={() => setOldVerOpen(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Computer</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(oldVerOpen?.terminals ?? []).map((t, i) => (
                <TableRow key={i}>
                  <TableCell><Typography variant="body2">{t.compName || "—"}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip
                      label={t.version || "Unknown"}
                      size="small"
                      color={t.version === termData?.MaxVersion ? "success" : "error"}
                      variant="outlined"
                      sx={{ fontSize: "0.72rem" }}
                    />
                  </TableCell>
                  <TableCell><Typography variant="body2">{fmtDateAU(t.lastAccess)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOldVerOpen(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ── Dispense Systems tab ────────────────────────────────────────────── */
function DispenseSystemsTab({ data }) {
  return (
    <Box sx={{ maxWidth: 560 }}>
      <ChartCard title="Dispense system split">
        {data?.DispensePie?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.DispensePie}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {data.DispensePie.map((entry, i) => (
                  <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Legend formatter={(value) => <Typography component="span" variant="caption">{value}</Typography>} />
              <ReTooltip contentStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="textSecondary">No data</Typography>
        )}
      </ChartCard>
    </Box>
  );
}

/* ── Installations tab ───────────────────────────────────────────────── */
function InstallationsTab({ data }) {
  return (
    <Box sx={{ maxWidth: 720 }}>
      <ChartCard title="Cumulative installs over time">
        {data?.InstallsByMonth?.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.InstallsByMonth} margin={{ top: 4, right: 4, left: -10, bottom: 44 }}>
              <XAxis
                dataKey="month"
                tick={{ fontSize: 9 }}
                axisLine={false}
                tickLine={false}
                interval={Math.ceil(data.InstallsByMonth.length / 8) - 1}
                angle={-45}
                textAnchor="end"
              />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <ReTooltip contentStyle={{ fontSize: 11 }} />
              <Line
                type="monotone"
                dataKey="count"
                name="Total installs"
                stroke={BLUE}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="textSecondary">No data</Typography>
        )}
      </ChartCard>
    </Box>
  );
}

/* ── Engagement Report tab ───────────────────────────────────────────── */
function EngagementTab() {
  const [app,         setApp]         = useState('quda');
  const [status,      setStatus]      = useState('active');
  const [maxActivity, setMaxActivity] = useState('');   // '' = no cap
  const [results,     setResults]     = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [copied,      setCopied]      = useState(false);

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    setResults(null);
    setCopied(false);
    try {
      const qs = new URLSearchParams({ app, status });
      if (maxActivity !== '') qs.set('max_activity', maxActivity);
      const res = await phappsRequest(`${API_PATHS.ADMIN_ENGAGEMENT_REPORT}?${qs}`, 'GET');
      setResults(res?.results ?? []);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, 'Failed to run engagement report'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyEmails = () => {
    if (!results?.length) return;
    const emails = results
      .map(r => r.Email)
      .filter(Boolean)
      .join(', ');
    navigator.clipboard.writeText(emails).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleExportCsv = () => {
    if (!results?.length) return;
    const header = 'Pharmacy,Email,Phone,State,Status,Activity (30d)';
    const rows = results.map(r =>
      [
        `"${(r.ChemistName || '').replace(/"/g, '""')}"`,
        r.Email   || '',
        r.Phone   || '',
        r.State   || '',
        r.Status  || '',
        r.Activity30d,
      ].join(',')
    );
    const csv  = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `engagement-${app}-${status}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const appLabel     = app === 'quda' ? 'orders' : 'deliveries';
  const emailCount   = (results || []).filter(r => r.Email).length;
  const noEmailCount = (results || []).length - emailCount;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 900 }}>
      {/* Filter card */}
      <Box sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BLUE, mb: 2 }}>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>

          {/* App */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>App</Typography>
            <Box sx={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
              {[['quda', 'Quda'], ['medidrop', 'MediDrop']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setApp(val)}
                  style={{
                    padding: '6px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    background: app === val ? BLUE : '#fff',
                    color:      app === val ? '#fff' : '#374151',
                    transition: 'background 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </Box>
          </Box>

          {/* Status */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
              Subscription status
            </Typography>
            <Box sx={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
              {[['active', 'Active'], ['trialing', 'Trial'], ['all', 'All']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setStatus(val)}
                  style={{
                    padding: '6px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    background: status === val ? BLUE : '#fff',
                    color:      status === val ? '#fff' : '#374151',
                    transition: 'background 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </Box>
          </Box>

          {/* Max activity */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 160 }}>
            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
              Max {appLabel} in last 30 days
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="number"
                min="0"
                value={maxActivity}
                onChange={(e) => setMaxActivity(e.target.value)}
                placeholder="No limit"
                style={{
                  width: 90,
                  padding: '6px 10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  fontSize: 13,
                  outline: 'none',
                }}
              />
              <Typography variant="caption" color="textSecondary">
                {maxActivity === '' ? '(show all)' : maxActivity === '0' ? '(zero only)' : `(≤ ${maxActivity})`}
              </Typography>
            </Box>
          </Box>

          {/* Run button */}
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={loading}
            sx={{
              backgroundColor: BLUE, color: '#fff', textTransform: 'none',
              fontWeight: 700, px: 3, alignSelf: 'flex-end',
              '&:hover': { backgroundColor: '#001a55' },
              '&:disabled': { opacity: 0.5 },
            }}
          >
            {loading ? 'Running…' : 'Run report'}
          </Button>
        </Box>

        {/* Preset quick-filters */}
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="textSecondary" sx={{ alignSelf: 'center', mr: 0.5 }}>
            Quick:
          </Typography>
          {[
            { label: 'Quda active — no orders',    app: 'quda',     status: 'active',   max: '0'  },
            { label: 'Quda active — under 30',     app: 'quda',     status: 'active',   max: '29' },
            { label: 'MediDrop active — no deliveries', app: 'medidrop', status: 'active', max: '0' },
            { label: 'MediDrop active — under 30', app: 'medidrop', status: 'active',   max: '29' },
          ].map((preset) => (
            <Chip
              key={preset.label}
              label={preset.label}
              size="small"
              variant="outlined"
              clickable
              onClick={() => { setApp(preset.app); setStatus(preset.status); setMaxActivity(preset.max); }}
              sx={{ fontSize: '0.72rem', borderColor: '#d1d5db', color: '#374151', '&:hover': { borderColor: BLUE, color: BLUE } }}
            />
          ))}
        </Box>
      </Box>

      {/* Error */}
      {error && <Alert severity="warning">{error}</Alert>}

      {/* Results */}
      {results !== null && (
        <Box sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BLUE }}>
                Results — {results.length} {results.length === 1 ? 'pharmacy' : 'pharmacies'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {emailCount} with email addresses{noEmailCount > 0 ? `, ${noEmailCount} without` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={emailCount === 0}
                onClick={handleCopyEmails}
                sx={{ textTransform: 'none', borderColor: copied ? '#059669' : BLUE, color: copied ? '#059669' : BLUE, fontWeight: 600 }}
              >
                {copied ? '✓ Copied!' : `Copy ${emailCount} email${emailCount !== 1 ? 's' : ''}`}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={results.length === 0}
                onClick={handleExportCsv}
                sx={{ textTransform: 'none', borderColor: BLUE, color: BLUE, fontWeight: 600 }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          {results.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No pharmacies match your filters.</Typography>
          ) : (
            <Box sx={{ height: Math.min(480, 56 + results.length * 36 + 56) }}>
              <DataGrid
                rows={results.map((r, i) => ({ id: i, ...r }))}
                columns={[
                  {
                    field: 'ChemistName', headerName: 'Pharmacy', flex: 1.4, minWidth: 160,
                    renderCell: (p) => (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: BLUE }}>{p.value || '—'}</Typography>
                    ),
                  },
                  { field: 'State',  headerName: 'State', width: 72,  align: 'center', headerAlign: 'center' },
                  {
                    field: 'Status', headerName: 'Sub status', width: 100,
                    renderCell: (p) => {
                      const v = (p.value || '').toLowerCase();
                      const color = v === 'active' ? 'success' : v === 'trialing' ? 'warning' : 'default';
                      return <Chip label={p.value || '—'} size="small" color={color} variant="outlined" sx={{ fontSize: '0.7rem' }} />;
                    },
                  },
                  {
                    field: 'Activity30d',
                    headerName: app === 'quda' ? 'Orders (30d)' : 'Deliveries (30d)',
                    width: 120,
                    type: 'number',
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (p) => (
                      <Chip
                        label={p.value}
                        size="small"
                        color={p.value === 0 ? 'error' : p.value < 10 ? 'warning' : 'default'}
                        variant={p.value === 0 ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.72rem', fontWeight: 700 }}
                      />
                    ),
                  },
                  {
                    field: 'Email', headerName: 'Email', flex: 1.5, minWidth: 180,
                    renderCell: (p) => p.value
                      ? <a href={`mailto:${p.value}`} style={{ color: BLUE, fontSize: 13 }}>{p.value}</a>
                      : <Typography variant="caption" color="textSecondary">—</Typography>,
                  },
                  { field: 'Phone', headerName: 'Phone', width: 130,
                    renderCell: (p) => p.value
                      ? <a href={`tel:${p.value}`} style={{ color: '#374151', fontSize: 13 }}>{p.value}</a>
                      : <Typography variant="caption" color="textSecondary">—</Typography>,
                  },
                ]}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                sx={pharmacyHaloDataGridSx}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/* ── Main page ───────────────────────────────────────────────────────── */
const AdminReportsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const [termLoading, setTermLoading] = useState(false);
  const [termError, setTermError] = useState(null);
  const [termData, setTermData] = useState(null);
  const termFetched = useRef(false);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.ADMIN_REPORTS, "GET");
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load reports"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const fetchTerminalAnalytics = useCallback(async () => {
    if (termFetched.current) return;
    termFetched.current = true;
    try {
      setTermLoading(true);
      setTermError(null);
      const res = await phappsRequest(API_PATHS.ADMIN_TERMINAL_ANALYTICS, "GET");
      setTermData(res);
    } catch (e) {
      termFetched.current = false;
      setTermError(getAdminApiErrorMessage(e, "Failed to load terminal analytics"));
    } finally {
      setTermLoading(false);
    }
  }, []);

  const handleTabChange = (_e, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) fetchTerminalAnalytics();
  };

  if (loading && !data) {
    return (
      <Box className="flex items-center justify-center min-h-[320px]">
        <Typography color="textSecondary">Loading reports…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex items-center justify-center min-h-[320px] px-4">
        <Alert severity="warning" sx={{ maxWidth: 560 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <div className="min-h-screen bg-white p-4 min-w-0 max-w-full">
      <Box sx={{ mb: 1 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIos fontSize="small" />}
          onClick={() => navigate("/admin")}
          sx={{ textTransform: "none", color: "text.secondary" }}
        >
          Back
        </Button>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 800, color: BLUE, mb: 2.5 }}>
        Admin Reports
      </Typography>

      {/* Tab bar */}
      <Box sx={{ borderBottom: "2px solid #e5e7eb", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { backgroundColor: BLUE, height: 3 } }}
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              minHeight: 42,
              color: "#6b7280",
              "&.Mui-selected": { color: BLUE },
            },
          }}
        >
          {TAB_LABELS.map((label) => (
            <Tab key={label} label={label} disableRipple />
          ))}
        </Tabs>
      </Box>

      {/* Tab panels */}
      {activeTab === 0 && <SmsBillingTab data={data} />}
      {activeTab === 1 && (
        <TerminalAnalyticsTab
          termData={termData}
          termLoading={termLoading}
          termError={termError}
          onLoad={fetchTerminalAnalytics}
        />
      )}
      {activeTab === 2 && <DispenseSystemsTab data={data} />}
      {activeTab === 3 && <InstallationsTab data={data} />}
      {activeTab === 4 && <EngagementTab />}
    </div>
  );
};

export default AdminReportsPage;
